import { useEffect, useMemo, useState } from "react";

export type MediaMetadata = {
  durationS: number;
  sizeMb: number;
  type: string;
};

/**
 * Stable key for a File based on properties that don't change during the
 * file's lifetime. Used for dedup and as a Map key in useFileDurations.
 */
export const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

/**
 * Read duration/size/type for a media file off-DOM. Creates a throwaway
 * `<video>`/`<audio>` element, awaits `loadedmetadata`, and revokes the
 * blob URL in `finally` so this never leaks even if the promise rejects.
 *
 * Chrome quirk: with `preload="metadata"`, some files (VBR mp3, m4a with
 * a trailing moov, webm/VP9, fragmented mp4) report `duration === Infinity`
 * at `loadedmetadata`. We force the browser to resolve the real duration
 * by seeking past the end and waiting for `durationchange` to fire with
 * a finite value. Bail after a timeout so the read never hangs.
 */
export async function readMediaMetadata(file: File): Promise<MediaMetadata> {
  const url = URL.createObjectURL(file);
  try {
    const el: HTMLMediaElement = file.type.startsWith("video")
      ? document.createElement("video")
      : document.createElement("audio");
    el.preload = "metadata";
    el.src = url;
    await new Promise<void>((resolve, reject) => {
      el.onloadedmetadata = () => resolve();
      el.onerror = () => reject(new Error("metadata read failed"));
    });

    if (!Number.isFinite(el.duration)) {
      await new Promise<void>((resolve) => {
        const cleanup = () => {
          el.removeEventListener("durationchange", onChange);
          clearTimeout(timeoutId);
        };
        const onChange = () => {
          if (Number.isFinite(el.duration)) {
            cleanup();
            resolve();
          }
        };
        const timeoutId = setTimeout(() => {
          cleanup();
          resolve();
        }, 3000);
        el.addEventListener("durationchange", onChange);
        // 1e9 seconds (~31 years) is past any real media; triggers a full
        // file scan in browsers that lazy-load duration.
        el.currentTime = 1e9;
      });
    }

    return {
      durationS: Number.isFinite(el.duration) ? el.duration : 0,
      sizeMb: file.size / 1024 / 1024,
      type: file.type,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Stable blob URL for a file. Revokes on file change and on unmount, so
 * a parent that displays N files at a time stays bounded at N live blobs.
 */
export function useObjectUrl(file: File | undefined): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

/**
 * Read durations (seconds) for a list of files. Memoized by the file
 * identity (name + size + lastModified) so the same File doesn't get
 * read twice as the list grows. Unreadable files map to 0.
 */
export function useFileDurations(files: File[]): Map<string, number> {
  const [durations, setDurations] = useState<Map<string, number>>(new Map());

  // Stable dep: re-run only when the set of files actually changes.
  const dep = useMemo(() => files.map(fileKey).join("|"), [files]);

  useEffect(() => {
    const unread = files.filter((f) => !durations.has(fileKey(f)));
    if (unread.length === 0) return;
    let cancelled = false;
    Promise.all(
      unread.map(async (f) => {
        try {
          const meta = await readMediaMetadata(f);
          return { key: fileKey(f), durationS: meta.durationS };
        } catch {
          return { key: fileKey(f), durationS: 0 };
        }
      })
    ).then((results) => {
      if (cancelled) return;
      setDurations((prev) => {
        const next = new Map(prev);
        for (const { key, durationS } of results) next.set(key, durationS);
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
    // `files` is intentionally read via closure; the gate is `dep`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);

  return durations;
}
